import { Issue } from "generated-graphql/graphql";
import { useMeasurements } from "hooks/useMeasurements";
import { useProducts } from "hooks/useProducts";
import { useMemo } from "react";
import { anySegment } from "util/issues";

import { InventoryLink } from "./InventoryLink";

function buildLink(
  tenantId: string,
  reportingYear: number,
  reportId?: string,
  facilityId?: string,
  chemicalId?: string,
  facilityChemicalId?: string,
  hasProducts?: boolean,
  hasMeasurements?: boolean,
  reportIsVerified?: boolean
) {
  const inStepper = !!reportId && !reportIsVerified;
  // Cases where we can't build a link, maybe because there is just products.
  if (
    (!inStepper && !facilityId) ||
    (!hasMeasurements && !facilityChemicalId) ||
    (!hasProducts && !chemicalId)
  ) {
    return null;
  }
  let link = `/o/${tenantId}/chemicals`;
  link += inStepper ? `/reports/${reportId}` : `/inventory/${facilityId}`;
  link += hasProducts ? "/other-chemicals" : `/chemicals`;
  link += hasMeasurements ? `/measurements/${chemicalId}` : `/${facilityChemicalId}`;
  link += inStepper ? "" : `?ry=${reportingYear}`;
  return link;
}

export const FacilityChemicalLink = ({
  facilityChemicalId,
  chemicalId,
  reportId,
  tenantId,
  facilityId,
  name,
  reportingYear,
  issues = [],
  reportIsVerified = false,
}: {
  facilityChemicalId: string;
  chemicalId: string;
  tenantId: string;
  name: string;
  reportingYear: number;
  issues?: Issue[];
  reportId?: string;
  facilityId?: string;
  reportIsVerified?: boolean;
}) => {
  const { loading: measurementsLoading, hasMeasurements } = useMeasurements(facilityId || "");
  const { loading: productsLoading, hasProducts } = useProducts();
  const link = buildLink(
    tenantId,
    reportingYear,
    reportId,
    facilityId,
    chemicalId,
    facilityChemicalId,
    hasProducts,
    hasMeasurements,
    reportIsVerified
  );
  const filteredIssues = useMemo(
    () =>
      issues.filter((issue) =>
        hasMeasurements
          ? anySegment(issue, (s) => s.model === "FacilityChemicalMeasurement")
          : anySegment(issue, (s) => s.model === "FacilityChemical")
      ),
    [issues, hasMeasurements]
  );

  const issueCount = filteredIssues.length;

  if (measurementsLoading || productsLoading || !link) {
    return null;
  }

  return <InventoryLink link={link} name={name} issueCount={issueCount} />;
};
